'use client';

import { motion } from 'framer-motion';
import { useRef, useState } from 'react';
import { Check, ArrowRight, Building, Globe2, Briefcase } from 'lucide-react';
import { Link } from 'react-router-dom';
import styles from './ServicesSection.module.css';

const services = [
    {
        title: "Mainland",
        desc: "100% ownership locally & globally.",
        icon: <Building size={24} />,
        features: [
            "Trade in UAE & Worldwide",
            "No Capital Required",
            "Government Contracts Eligible"
        ],
        link: "/business-setup/mainland",
        image: "/images/mainland.webp"
    },
    {
        title: "Freezone",
        desc: "Tax-free international hub.",
        icon: <Briefcase size={24} />,
        features: [
            "100% Tax Exemption",
            "No Physical Office Needed",
            "Remote Setup Available"
        ],
        link: "/business-setup/freezone",
        image: "/images/freezone.webp"
    },
    {
        title: "Offshore",
        desc: "Asset protection & privacy.",
        icon: <Globe2 size={24} />,
        features: [
            "Global Bank Accounts",
            "Complete Anonymity",
            "International Trading"
        ],
        link: "/business-setup/offshore",
        image: "/images/offshore.webp"
    }
];

export default function ServicesSection() {
    return (
        <section className={styles.section} id="jurisdiction" suppressHydrationWarning>
            <div className="container">

                <div className={styles.header}>
                    <motion.h2
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        className={styles.title}
                    >
                        Select Your <span style={{ color: 'var(--rb-green)' }}>Jurisdiction</span>
                    </motion.h2>
                    <p className={styles.subtitle}>
                        RotanaBiz offers compliant pathways for every business scale.
                    </p>
                </div>

                <div className={styles.grid}>
                    {services.map((service, i) => (
                        <ServiceCard key={i} service={service} index={i} />
                    ))}
                </div>

            </div>
        </section>
    );
}

function ServiceCard({ service, index }: any) {
    const cardRef = useRef<HTMLDivElement>(null);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!cardRef.current) return;
        const rect = cardRef.current.getBoundingClientRect();
        setMousePos({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
        });
    };

    return (
        <motion.div
            ref={cardRef}
            className={`${styles.card}`}
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            viewport={{ once: true }}
            onMouseMove={handleMouseMove}
        >
            <div
                className={styles.cardGlow}
                style={{ left: mousePos.x, top: mousePos.y }}
            />
            <div
                className={styles.cardBg}
                style={{ backgroundImage: `url(${service.image})` }}
            />
            <div className={styles.overlay} />

            <div className={styles.cardInner}>
                <div className={styles.content}>
                    <div className={styles.iconWrapper}>
                        {service.icon}
                    </div>

                    <h3 className={styles.cardTitle}>{service.title}</h3>
                    <p className={styles.cardDesc}>{service.desc}</p>

                    <div className={styles.features}>
                        {service.features.map((feat: string, idx: number) => (
                            <div key={idx} className={styles.featureItem}>
                                <Check size={16} style={{ color: 'var(--rb-green)' }} /> {feat}
                            </div>
                        ))}
                    </div>

                    <Link to={service.link} className={styles.actionBtn}>
                        Start Setup <ArrowRight size={16} />
                    </Link>
                </div>
            </div>
        </motion.div>
    );
}
